import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SiteHeader, SiteFooter, useMeta } from "@/components/site/Chrome";

const COPY = {
  paid: { kicker: "PAYMENT CONFIRMED", title: "You're in.", body: "Your access is active. Sign in with the email you used at checkout — we send a one-time code, no password." },
  failed: { kicker: "PAYMENT", title: "Payment didn't complete.", body: "No charge was made. You can try again anytime; the cert page is one click away." },
  timeout: { kicker: "PAYMENT", title: "Still processing.", body: "Check your email — access activates automatically the moment payment confirms, and we send you a sign-in link." },
  cancelled: { kicker: "CHECKOUT", title: "Checkout cancelled.", body: "No charge was made. Your discount code, if you used one, still works." },
};

export default function PaymentStatus({ cancel }) {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(cancel ? "cancelled" : "checking");
  useMeta("Payment — ExamRay");

  useEffect(() => {
    if (cancel) return;
    const sid = params.get("session_id");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") return setStatus("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setStatus("failed");
      } catch (e) { console.warn("payment status poll failed", e); }
      if (++tries < 10) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [cancel, params]);

  const c = COPY[status];
  const testid = { paid: "payment-success", failed: "payment-failed", timeout: "payment-failed", cancelled: "payment-cancelled" }[status];
  return (
    <div className="er" data-testid="payment-page">
      <SiteHeader />
      <section className="er-page" style={{ maxWidth: 640 }}>
        <div className="er-buybox">
          <div className="er-rule" />
          <div style={{ padding: "30px 28px" }}>
            {status === "checking" ? (
              <>
                <p className="er-label" style={{ color: "var(--ink-4)" }}>PAYMENT</p>
                <h1 className="er-h2" style={{ marginTop: 12, fontSize: 24 }} data-testid="payment-checking">Confirming your payment…</h1>
                <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.6, color: "var(--ink-3)" }}>Stripe is telling us it went through. This usually takes a few seconds.</p>
              </>
            ) : (
              <div data-testid={testid}>
                <p className="er-label" style={{ color: status === "paid" ? "var(--ac-deep)" : "var(--ink-4)" }}>{c.kicker}</p>
                <h1 className="er-display" style={{ marginTop: 12, fontWeight: 900, fontSize: 34, letterSpacing: "-0.03em", lineHeight: 1.05 }}>{c.title}</h1>
                <p style={{ marginTop: 12, fontSize: 15.5, lineHeight: 1.6, color: "var(--ink-3)" }}>{c.body}</p>
                <div style={{ marginTop: 24, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {status === "paid" ? (
                    <Link to="/login" data-testid="payment-login-btn" className="er-btn" style={{ padding: "13px 26px" }}>Sign in now</Link>
                  ) : (
                    <Link to="/#catalog" data-testid="payment-home-link" className="er-btn-secondary">Back to certifications</Link>
                  )}
                  {status !== "paid" && <a href="mailto:support@examray.com" className="er-btn-secondary" data-testid="payment-support-link">Email support</a>}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
